import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { whatsappLink } from '@/mocks/gymInfo';

const navItems = [
  { key: 'nav.home', href: '/' },
  { key: 'nav.structure', href: '/estrutura' },
  { key: 'nav.modalities', href: '/modalidades' },
  { key: 'nav.plans', href: '#planos' },
  { key: 'nav.store', href: '#loja' },
  { key: 'nav.schedules', href: '/horarios' },
  { key: 'nav.location', href: '/localizacao' },
  { key: 'nav.contact', href: '/contato' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith('#')) {
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate('/');
        setTimeout(() => {
          const target = document.getElementById(id);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
      return;
    }
    navigate(href);
  };

  const toggleLang = () => {
    const next = i18n.language === 'pt' ? 'en' : 'pt';
    i18n.changeLanguage(next);
  };

  return (
    <header
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-foreground-950/95 backdrop-blur-sm' : 'bg-transparent'
      }`}
    >
      <nav className="w-full flex items-center justify-between px-4 md:px-6 py-3 md:py-4" role="navigation" aria-label={t('nav.mainNavigation')}>
        <button
          onClick={() => handleNav('/')}
          className="font-heading text-2xl md:text-3xl text-background-50 tracking-wider"
          aria-label="Top Gym Cumbuco - Home"
        >
          TOP GYM
        </button>

        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => handleNav(item.href)}
              className="nav-link-hover text-base font-body text-background-50/80 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t(item.key)}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={toggleLang}
            className="btn-hover-press text-base font-body text-background-50/60 hover:text-background-50 transition-colors px-2 py-1"
            aria-label={i18n.language === 'pt' ? 'Switch to English' : 'Mudar para Português'}
          >
            {i18n.language === 'pt' ? 'EN' : 'PT'}
          </button>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-hover inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-body text-base font-semibold px-5 py-2.5 rounded-full whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-base" aria-hidden="true"></i>
            {t('nav.whatsapp')}
          </a>
        </div>

        <button
          className="md:hidden text-background-50 w-11 h-11 flex items-center justify-center"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          aria-expanded={mobileOpen}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} aria-hidden="true"></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-foreground-950/95 backdrop-blur-sm border-t border-foreground-900/50 px-4 py-6 space-y-4" role="menu">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => handleNav(item.href)}
              className="block w-full text-left text-base font-body text-background-50/80 hover:text-primary-500 transition-colors py-2"
              role="menuitem"
            >
              {t(item.key)}
            </button>
          ))}
          <div className="flex items-center gap-4 pt-2 border-t border-foreground-900/50">
            <button
              onClick={toggleLang}
              className="btn-hover-press text-base font-body text-background-50/60 hover:text-background-50 transition-colors px-2 py-1"
              aria-label={i18n.language === 'pt' ? 'Switch to English' : 'Mudar para Português'}
            >
              {i18n.language === 'pt' ? 'EN' : 'PT'}
            </button>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-hover inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-body text-base font-semibold px-4 py-2 rounded-full whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base" aria-hidden="true"></i>
              {t('nav.whatsapp')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}